<template>
  <header class="border-b border-[#f1f5f9] bg-white">
    <nav aria-label="Primary navigation" class="mx-auto flex h-[56px] w-[calc(100%-32px)] max-w-[1272px] items-center justify-between gap-[24px] sm:w-[calc(100%-48px)]">
      <a :href="dashboardHref" class="shrink-0 text-[18px] font-bold tracking-tight text-[#0f172b]">SCREENED</a>

      <div class="hidden items-center gap-[32px] md:flex">
        <a v-if="contractorPortal.enabled" :href="dashboardHref" class="text-[14px] text-[#62748e] transition-colors hover:text-[#00786f]">Dashboard</a>
        <router-link v-else to="/contractor/profile" class="text-[14px] text-[#62748e] transition-colors hover:text-[#00786f]">Dashboard</router-link>
        <router-link to="/contractor/profile/preview" class="text-[14px] text-[#62748e] transition-colors hover:text-[#00786f]">Profile</router-link>
        <router-link to="/contractor/jobs" class="text-[14px] text-[#62748e] transition-colors hover:text-[#00786f]">Job Search</router-link>
        <router-link to="/contractor/talent-communities" class="text-[14px] text-[#62748e] transition-colors hover:text-[#00786f]">Talent Communities</router-link>
        <router-link v-if="canSearchCompanies" to="/contractor/companies" class="text-[14px] text-[#62748e] transition-colors hover:text-[#00786f]">Search Companies</router-link>
      </div>

      <div class="flex items-center gap-[6px]">
        <button type="button" class="flex h-[44px] w-[44px] items-center justify-center rounded-[8px] text-[#314158] hover:bg-[#f1f5f9]" aria-label="Notifications">
          <svg class="h-[16px] w-[16px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></svg>
        </button>
        <div class="relative">
          <button type="button" class="flex min-h-[44px] items-center gap-[8px] rounded-[8px] px-[8px] text-[14px] text-[#314158] hover:bg-[#f1f5f9]" aria-label="Open account menu" aria-haspopup="menu" :aria-expanded="userMenuOpen" @click="userMenuOpen = !userMenuOpen">
            <span class="flex h-[32px] w-[32px] items-center justify-center rounded-full bg-[#f0fdfa] text-[14px] font-semibold text-[#00786f]">{{ contractorInitial }}</span>
            <span class="hidden sm:inline">{{ contractorName }}</span>
            <svg class="hidden h-[16px] w-[16px] sm:block" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4 4a.75.75 0 01-1.06 0l-4-4a.75.75 0 01.02-1.06z" clip-rule="evenodd" /></svg>
          </button>
          <div v-if="userMenuOpen" class="absolute right-0 z-50 mt-[8px] w-[224px] rounded-[8px] border border-[#e2e8f0] bg-white py-[4px] text-[14px] text-[#314158] shadow-lg" role="menu">
            <div class="border-b border-[#e2e8f0] px-[16px] py-[8px] font-medium">{{ contractorName }}</div>
            <router-link to="/contractor/profile/edit" class="block px-[16px] py-[8px] hover:bg-[#f1f5f9]" role="menuitem" @click="userMenuOpen = false">Edit profile</router-link>
            <router-link to="/contractor/settings" class="block px-[16px] py-[8px] hover:bg-[#f1f5f9]" role="menuitem" @click="userMenuOpen = false">Settings</router-link>
            <div class="my-1 border-t border-[#e2e8f0]"></div>
            <router-link to="/contractor/logout" class="block px-[16px] py-[8px] hover:bg-[#f1f5f9]" role="menuitem">Sign out</router-link>
          </div>
        </div>

        <div class="relative md:hidden">
          <button type="button" class="flex min-h-11 items-center rounded-lg px-2 text-sm text-[#314158] hover:bg-[#f1f5f9]" aria-label="Open navigation menu" aria-haspopup="menu" :aria-expanded="mobileMenuOpen" @click="mobileMenuOpen = !mobileMenuOpen">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" d="M4 7h16M4 12h16M4 17h16" /></svg>
          </button>
          <div v-if="mobileMenuOpen" class="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-[#e2e8f0] bg-white py-1 text-sm text-[#314158] shadow-lg" role="menu">
            <a v-if="contractorPortal.enabled" :href="dashboardHref" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem">Dashboard</a>
            <router-link v-else to="/contractor/profile" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Dashboard</router-link>
            <router-link to="/contractor/profile/preview" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Profile</router-link>
            <router-link to="/contractor/jobs" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Job Search</router-link>
            <router-link to="/contractor/talent-communities" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Talent Communities</router-link>
            <router-link v-if="canSearchCompanies" to="/contractor/companies" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Search Companies</router-link>
            <router-link to="/contractor/profile/edit" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Edit profile</router-link>
            <router-link to="/contractor/settings" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem" @click="mobileMenuOpen = false">Settings</router-link>
            <div class="my-1 border-t border-[#e2e8f0]"></div>
            <router-link to="/contractor/logout" class="block px-4 py-2 hover:bg-[#f1f5f9]" role="menuitem">Sign out</router-link>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { getContractorMiningCompanySearchAccess } from '@/api/contractor/company'
import { contractorPortal } from '@/config/contractorPortal'

export default {
  name: 'ContractorHeader',
  data() {
    return {
      contractorPortal,
      mobileMenuOpen: false,
      userMenuOpen: false,
      canSearchCompanies: false,
    }
  },
  computed: {
    dashboardHref() {
      return contractorPortal.enabled ? contractorPortal.dashboardUrl : '/contractor/profile'
    },
    contractorName() {
      return this.$store.state.contractorName || 'Guest'
    },
    contractorInitial() {
      return this.contractorName.trim().charAt(0).toUpperCase() || 'G'
    },
  },
  created() {
    this.loadCompanySearchAccess()
  },
  methods: {
    async loadCompanySearchAccess() {
      const cacheKey = 'contractorCompanySearchAccess'
      const cacheAtKey = 'contractorCompanySearchAccessAt'
      const cached = sessionStorage.getItem(cacheKey)
      const cachedAt = Number(sessionStorage.getItem(cacheAtKey) || 0)
      if (cached !== null && !Number.isNaN(cachedAt) && Date.now() - cachedAt < 300000) {
        this.canSearchCompanies = cached === 'true'
        return
      }

      try {
        const { data } = await getContractorMiningCompanySearchAccess()
        this.canSearchCompanies = !!data?.allowed
        sessionStorage.setItem(cacheKey, this.canSearchCompanies ? 'true' : 'false')
        sessionStorage.setItem(cacheAtKey, Date.now().toString())
      } catch (error) {
        this.canSearchCompanies = false
        sessionStorage.setItem(cacheKey, 'false')
        sessionStorage.setItem(cacheAtKey, Date.now().toString())
      }
    },
  },
}
</script>
